import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  color: #333;
  line-height: 1.7;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
`;

const AppName = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 2rem;
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #888;
  margin-bottom: 3rem;
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #1a1a1a;
`;

const Paragraph = styled.p`
  margin-bottom: 1rem;
  color: #444;
`;

const List = styled.ul`
  margin-bottom: 1rem;
  padding-left: 1.5rem;

  li {
    margin-bottom: 0.5rem;
    color: #444;
  }
`;

const ContactLink = styled.a`
  color: #007AFF;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 3rem 0;
`;

const TimeToGoMacTerms: React.FC = () => {
  return (
    <Container>
      <Title>Privacy & Terms</Title>
      <AppName>TimeToGo for Mac</AppName>
      <LastUpdated>Last updated: <time dateTime="2026-10-03">October 3, 2026</time></LastUpdated>

      {/* Privacy Policy */}
      <SectionTitle>Privacy Policy</SectionTitle>

      <Section>
        <Paragraph>
          TimeToGo for Mac is a menu bar app that helps you monitor commute times.
          We are committed to protecting your privacy and being transparent about our data practices.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Data Collection</SectionTitle>
        <Paragraph>
          <strong>Your saved routes and addresses are stored locally on your Mac.</strong>
        </Paragraph>
        <Paragraph>
          TimeToGo has no account system or backend for storing your routes, and the Mac app has
          no advertising. Address searches and route requests go directly to Apple, while usage
          analytics go to Google/Firebase. See our{' '}
          <ContactLink href="/timetogo/privacy">full Privacy Policy</ContactLink>{' '}
          for data collection details and privacy choices.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Local Storage</SectionTitle>
        <Paragraph>
          The app uses your Mac's local storage to save:
        </Paragraph>
        <List>
          <li>Your saved routes (origin and destination locations)</li>
          <li>Your acceptable commute time preferences</li>
          <li>Schedule settings for automated monitoring</li>
          <li>Trial start date (stored in Keychain)</li>
        </List>
        <Paragraph>
          You can remove saved routes in the app. Deleting the app may not remove all local
          preferences or the Keychain trial record, and does not delete analytics already sent to Google.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Third-Party Services</SectionTitle>
        <Paragraph>
          <strong>Apple MapKit and Location Services:</strong> Apple receives address search queries
          and route endpoints to provide maps and travel-time estimates. If you select a current-location
          endpoint, TimeToGo may obtain your location during setup or when monitoring starts, including
          scheduled starts. This is optional; you can use fixed addresses and manage location permission
          in macOS Privacy &amp; Security settings. Requests go directly to Apple, not a TimeToGo backend.
        </Paragraph>
        <Paragraph>
          <strong>Google Analytics for Firebase:</strong> Release builds send usage events for route
          saving, monitoring starts, upgrade screen views (trial status), and completed purchases
          (yearly or lifetime plan) to Google to help us understand usage and improve the app.
          Our custom events contain no route names, addresses, search queries, or precise coordinates.
          The SDK also collects an app-instance identifier, app/device information, and automatic
          usage events, and may measure StoreKit purchases. Google derives general location from
          masked IP addresses. These identifiers mean analytics is not fully anonymous.
        </Paragraph>
        <Paragraph>
          Analytics is disabled in Debug builds and Mac App Preview demo mode. The current Mac
          app has no in-app analytics opt-out; denying location permission does not disable analytics.
          For more information, see{' '}
          <ContactLink href="https://support.google.com/analytics/answer/10285841" target="_blank" rel="noopener noreferrer">
            Google's analytics data collection disclosures
          </ContactLink>{' '}and{' '}
          <ContactLink href="https://www.apple.com/legal/privacy/data/en/apple-maps/" target="_blank" rel="noopener noreferrer">
            Apple Maps &amp; Privacy
          </ContactLink>.
        </Paragraph>
        <Paragraph>
          <strong>Apple StoreKit:</strong> In-app purchases and subscriptions are processed entirely by Apple.
          We do not have access to your payment information. Please review{' '}
          <ContactLink href="https://www.apple.com/legal/internet-services/itunes/" target="_blank" rel="noopener noreferrer">
            Apple's Media Services Terms and Conditions
          </ContactLink>{' '}
          for details.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Changes to This Policy</SectionTitle>
        <Paragraph>
          We may update this Privacy Policy from time to time. Any changes will be reflected on this page
          with an updated revision date.
        </Paragraph>
      </Section>

      <Divider />

      {/* Terms of Use */}
      <SectionTitle>Terms of Use</SectionTitle>

      <Section>
        <SectionTitle>Free Trial</SectionTitle>
        <Paragraph>
          TimeToGo for Mac offers a 7-day free trial. During the trial period, you have full access
          to all features. After the trial expires, a subscription or one-time purchase is required
          to continue using premium features (auto-refresh, scheduled monitoring, notifications,
          and launch at login).
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Subscriptions</SectionTitle>
        <Paragraph>
          TimeToGo for Mac offers the following purchase options:
        </Paragraph>
        <List>
          <li><strong>Yearly Subscription:</strong> Auto-renews annually. You can cancel anytime
            via System Settings → Apple ID → Subscriptions.</li>
          <li><strong>Lifetime Purchase:</strong> A one-time payment for permanent access to all features.</li>
        </List>
        <Paragraph>
          Payment is charged to your Apple ID account at confirmation of purchase. Subscriptions
          automatically renew unless cancelled at least 24 hours before the end of the current period.
          Your account will be charged for renewal within 24 hours prior to the end of the current period.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Disclaimer</SectionTitle>
        <Paragraph>
          Travel time estimates provided by TimeToGo may not always reflect actual traffic conditions.
          TimeToGo is intended as an informational tool
          and should not be relied upon as the sole source for commute planning.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Contact Us</SectionTitle>
        <Paragraph>
          If you have any questions about these terms or our privacy practices, please contact us at:{' '}
          <ContactLink href="mailto:support@ai4u.co.nz">support@ai4u.co.nz</ContactLink>
        </Paragraph>
      </Section>
    </Container>
  );
};

export default TimeToGoMacTerms;
